import { Compass, Eye, MapPin, Target } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_abule-imole/artifacts/r3juwlyz_WhatsApp%20Image%202026-04-16%20at%2022.07.57.jpeg",
  location: "https://customer-assets.emergentagent.com/job_abule-imole/artifacts/ca8df3sw_WhatsApp%20Image%202026-04-16%20at%2023.26.50.jpeg",
};

const philosophyIcons = [Compass, Eye, Target];
const philosophyKeys = ["community", "sustainability", "development"];
const philosophyAccents = ["#1B3D2F", "#C4883A", "#A3B18A"];

const pillarData = [
  { id: "foundation", number: "01", key: "foundation", accent: "#1B3D2F" },
  { id: "cooperative", number: "02", key: "cooperative", accent: "#C4883A" },
  { id: "devco", number: "03", key: "devco", accent: "#A3B18A" },
  { id: "enterprises", number: "04", key: "enterprises", accent: "#1C2333" },
  { id: "school", number: "05", key: "school", accent: "#C8C5BC" },
];

function AboutHero() {
  const { t } = useLanguage();
  return (
    <section data-testid="about-hero" className="bg-[#C8C5BC]/30 pt-28 sm:pt-48 pb-20 sm:pb-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <p className="text-base sm:text-lg tracking-[0.2em] uppercase font-semibold text-[#C4883A] mb-4 animate-fade-in-up opacity-0">{t.about.label}</p>
        <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl lg:text-6xl tracking-tight font-light text-[#1C2333] max-w-3xl mx-auto leading-tight animate-fade-in-up opacity-0 animate-delay-100">
          {t.about.heroTitle1}<br /><span className="font-medium text-[#1B3D2F]">{t.about.heroTitle2}</span>
        </h1>
        <p className="mt-8 text-[#1C2333]/70 text-lg sm:text-xl lg:text-2xl max-w-2xl mx-auto leading-relaxed animate-fade-in-up opacity-0 animate-delay-200">{t.about.heroBody}</p>
      </div>
    </section>
  );
}

function WhoWeAreSection() {
  const { t } = useLanguage();
  return (
    <section data-testid="who-we-are-section" className="py-20 sm:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-5">
            <div className="overflow-hidden"><img src={IMAGES.hero} alt="Community" className="w-full h-80 lg:h-[480px] object-cover" /></div>
          </div>
          <div className="lg:col-span-7">
            <p className="text-base sm:text-lg tracking-[0.2em] uppercase font-semibold text-[#C4883A] mb-4">{t.about.whoWeAreLabel}</p>
            <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-medium tracking-tight text-[#1B3D2F] mb-6">{t.about.whoWeAreTitle}</h2>
            <p className="text-lg sm:text-xl leading-relaxed text-[#1C2333]/80 mb-6">{t.about.whoWeAreBody1}</p>
            <p className="text-lg sm:text-xl leading-relaxed text-[#1C2333]/80">{t.about.whoWeAreBody2}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function PhilosophySection() {
  const { t } = useLanguage();
  return (
    <section data-testid="philosophy-section" className="py-20 sm:py-28 bg-[#D0D5C8]/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <p className="text-base sm:text-lg tracking-[0.2em] uppercase font-semibold text-[#C4883A] mb-4">{t.about.philosophyLabel}</p>
          {t.about.philosophyTitle && <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-medium tracking-tight text-[#1B3D2F] mb-6">{t.about.philosophyTitle}</h2>}
          <p className="text-lg sm:text-xl leading-relaxed text-[#1C2333]/80">{t.about.philosophyBody}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {philosophyKeys.map((key, idx) => {
            const Icon = philosophyIcons[idx];
            const accent = philosophyAccents[idx];
            return (
              <div key={key} data-testid={`philosophy-card-${key}`} className="group bg-white p-10 border border-[#C8C5BC]/30 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-500 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1" style={{ backgroundColor: accent }} />
                <div className="w-14 h-14 flex items-center justify-center mb-8" style={{ backgroundColor: `${accent}15`, color: accent }}><Icon size={24} /></div>
                <h3 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl font-semibold text-[#1C2333] mb-4">{t.about[key]}</h3>
                <p className="text-lg sm:text-xl leading-relaxed text-[#1C2333]/70">{t.about[`${key}Desc`]}</p>
                <div className="mt-6 w-8 h-[2px] bg-[#C4883A] group-hover:w-16 transition-all duration-500" />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function PillarsSection() {
  const { t } = useLanguage();
  return (
    <section data-testid="pillars-section" className="py-20 sm:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <p className="text-base sm:text-lg tracking-[0.2em] uppercase font-semibold text-[#C4883A] mb-4">{t.about.pillarsLabel}</p>
          <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-medium tracking-tight text-[#1B3D2F] mb-6">{t.about.pillarsTitle}</h2>
          <p className="text-lg sm:text-xl leading-relaxed text-[#1C2333]/80">{t.about.pillarsBody}</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
          {pillarData.map((p) => (
            <div key={p.id} data-testid={`pillar-card-${p.id}`} className="group relative p-8 border border-[#C8C5BC]/30 hover:shadow-lg hover:-translate-y-1 transition-all duration-500 overflow-hidden">
              <span className="font-['Cormorant_Garamond'] text-5xl sm:text-6xl font-bold text-[#A3B18A]/20 absolute top-4 right-4 leading-none">{p.number}</span>
              <div className="relative">
                <div className="w-1 h-10 mb-6" style={{ backgroundColor: p.accent }} />
                <h3 className="font-['Cormorant_Garamond'] text-xl sm:text-2xl font-semibold text-[#1C2333] mb-3">{t.about[p.key]}</h3>
                <p className="text-base sm:text-lg leading-relaxed text-[#1C2333]/70">{t.about[`${p.key}Desc`]}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhereWeAreSection() {
  const { t } = useLanguage();
  return (
    <section data-testid="where-we-are-section" className="py-20 sm:py-28 bg-[#C8C5BC]/15">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <p className="text-base sm:text-lg tracking-[0.2em] uppercase font-semibold text-[#C4883A] mb-4">{t.about.whereLabel}</p>
            <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-medium tracking-tight text-[#1B3D2F] mb-6">{t.about.whereTitle}</h2>
            <p className="text-lg sm:text-xl leading-relaxed text-[#1C2333]/80 mb-6">{t.about.whereBody1}</p>
            <p className="text-lg sm:text-xl leading-relaxed text-[#1C2333]/80 mb-6">{t.about.whereBody2}</p>
            <div className="flex items-center gap-3 text-[#1B3D2F]">
              <MapPin size={18} className="text-[#C4883A]" />
              <span className="text-base sm:text-lg font-medium">{t.about.wherePin}</span>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="overflow-hidden"><img src={IMAGES.location} alt="Village" className="w-full h-80 lg:h-[420px] object-cover" /></div>
          </div>
        </div>
      </div>
    </section>
  );
}

function MissionVisionSection() {
  const { t } = useLanguage();
  return (
    <section data-testid="mission-vision-section" className="py-20 sm:py-32 bg-[#D0D5C8]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 lg:gap-24">
          <div>
            <p className="text-base sm:text-lg tracking-[0.2em] uppercase font-semibold text-[#C4883A] mb-4">{t.about.missionLabel}</p>
            <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-medium tracking-tight text-[#1C2333] mb-6">{t.about.missionTitle}</h2>
            <p className="text-[#1C2333]/70 text-lg sm:text-xl leading-relaxed">{t.about.missionBody}</p>
          </div>
          <div>
            <p className="text-base sm:text-lg tracking-[0.2em] uppercase font-semibold text-[#C4883A] mb-4">{t.about.visionLabel}</p>
            <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-medium tracking-tight text-[#1C2333] mb-6">{t.about.visionTitle}</h2>
            <p className="text-[#1C2333]/70 text-lg sm:text-xl leading-relaxed">{t.about.visionBody}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function About() {
  return (
    <div data-testid="about-page">
      <AboutHero />
      <WhoWeAreSection />
      <PhilosophySection />
      <PillarsSection />
      <WhereWeAreSection />
      <MissionVisionSection />
    </div>
  );
}
